import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { apiClient, API_ENDPOINTS, useAuthStore } from '@dukene/shared';
import { X, Loader2, Smartphone, Building2, Phone, Hash, AlertCircle } from 'lucide-react';

interface ConnectProviderModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ConnectProviderModal({ isOpen, onClose }: ConnectProviderModalProps) {
    const queryClient = useQueryClient();
    const activeStoreId = useAuthStore((state) => state.activeStoreId);
    const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
        defaultValues: {
            channelType: 'TILL',
            channelNumber: '',
            accountNumber: '',
            businessName: '',
            ownerPhone: '',
            bankPaybillId: '',
            bankName: '',
        }
    });

    const channelType = watch('channelType');

    const { data: banksResponse, isLoading: isBanksLoading } = useQuery({
        queryKey: ['merchant-bank-paybills'],
        queryFn: () => apiClient.get<any>(API_ENDPOINTS.MERCHANT.CHANNELS.BANKS),
        enabled: isOpen && channelType === 'BANK',
    });
    const banksList = banksResponse?.banks || (Array.isArray(banksResponse) ? banksResponse : []);

    useEffect(() => {
        if (isOpen) {
            reset({
                channelType: 'TILL',
                channelNumber: '',
                accountNumber: '',
                businessName: '',
                ownerPhone: '',
                bankPaybillId: '',
                bankName: '',
            });
        }
    }, [isOpen, reset]);

    const registerMutation = useMutation({
        mutationFn: (data: any) =>
            apiClient.post(API_ENDPOINTS.MERCHANT.CHANNELS.REGISTER, {
                channelType: data.channelType,
                channelNumber: data.channelNumber.trim(),
                accountNumber: (data.channelType === 'PAYBILL' || data.channelType === 'BANK') ? (data.accountNumber?.trim() || null) : null,
                businessName: data.businessName.trim(),
                ownerPhone: data.ownerPhone.trim(),
                bankPaybillId: data.channelType === 'BANK' ? (data.bankPaybillId?.trim() || null) : null,
                bankName: data.channelType === 'BANK' ? (data.bankName?.trim() || null) : null,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['merchant-channels', activeStoreId] });
            onClose();
        },
    });

    if (!isOpen) return null;

    const onSubmit = (data: any) => {
        registerMutation.mutate(data);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald/10 text-emerald flex items-center justify-center font-bold">
                            <Smartphone className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">Add M-Pesa Payment Channel</h3>
                            <p className="text-xs text-gray-500">Connect your Buy Goods Till or Paybill</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close payment channel modal"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
                    {/* Channel Type Selector */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Channel Type</label>
                        <div className="grid grid-cols-3 gap-1.5 p-1 bg-gray-100 rounded-xl">
                            <button
                                type="button"
                                onClick={() => reset({ ...watch(), channelType: 'TILL', accountNumber: '', bankPaybillId: '', bankName: '', channelNumber: '' })}
                                className={`py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1 ${
                                    channelType === 'TILL' ? 'bg-white text-emerald shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <span>Buy Goods</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => reset({ ...watch(), channelType: 'PAYBILL', bankPaybillId: '', bankName: '', channelNumber: '' })}
                                className={`py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1 ${
                                    channelType === 'PAYBILL' ? 'bg-white text-emerald shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <span>Paybill</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => reset({ ...watch(), channelType: 'BANK', channelNumber: '', accountNumber: '', bankPaybillId: '', bankName: '' })}
                                className={`py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1 ${
                                    channelType === 'BANK' ? 'bg-white text-emerald shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <span>Bank Account</span>
                            </button>
                        </div>
                    </div>

                    {/* Bank Selection Dropdown (if BANK) */}
                    {channelType === 'BANK' && (
                        <div className="space-y-1 animate-in slide-in-from-top-2 duration-200">
                            <label htmlFor="bank-paybill-id" className="text-xs font-bold text-gray-700 flex items-center gap-1">
                                <Building2 className="w-3.5 h-3.5 text-gray-400" /> Select Bank Institution
                            </label>
                            <select
                                id="bank-paybill-id"
                                value={watch('bankPaybillId') || ''}
                                onChange={(e) => {
                                    const selectedId = e.target.value;
                                    const bank = banksList.find((b: any) => String(b.id) === String(selectedId));
                                    if (bank) {
                                        setValue('bankPaybillId', bank.id);
                                        setValue('bankName', bank.name);
                                        setValue('channelNumber', bank.paybill);
                                    } else {
                                        setValue('bankPaybillId', '');
                                        setValue('bankName', '');
                                        setValue('channelNumber', '');
                                    }
                                }}
                                className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-emerald focus:ring-1 focus:ring-emerald outline-none transition-all bg-white"
                                required={channelType === 'BANK'}
                            >
                                <option value="">-- Choose a Bank --</option>
                                {isBanksLoading ? (
                                    <option disabled>Loading banks...</option>
                                ) : (
                                    banksList.map((b: any) => (
                                        <option key={b.id} value={b.id}>
                                            {b.name} (Paybill: {b.paybill})
                                        </option>
                                    ))
                                )}
                            </select>
                            {!watch('bankPaybillId') && (
                                <span className="text-[11px] text-amber-600 font-medium">Please select your bank institution from the list</span>
                            )}
                        </div>
                    )}

                    {/* Business Name */}
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-700 flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5 text-gray-400" /> Business Name (as registered with M-Pesa)
                        </label>
                        <input
                            {...register('businessName', { required: 'Business Name is required' })}
                            type="text"
                            placeholder="e.g. Dukene Store Enterprise"
                            className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-emerald focus:ring-1 focus:ring-emerald outline-none transition-all"
                        />
                        {errors.businessName && <span className="text-[11px] text-red-500 font-medium">{errors.businessName.message as string}</span>}
                    </div>

                    {/* Channel Number (Till / Paybill / Bank Shortcode) */}
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-700 flex items-center gap-1">
                            <Hash className="w-3.5 h-3.5 text-gray-400" /> {channelType === 'TILL' ? 'M-Pesa Till Number' : channelType === 'BANK' ? 'Bank M-Pesa Paybill Shortcode' : 'M-Pesa Paybill Number'}
                        </label>
                        <input
                            {...register('channelNumber', { required: `${channelType === 'TILL' ? 'Till Number' : channelType === 'BANK' ? 'Bank Paybill Shortcode' : 'Paybill Number'} is required` })}
                            type="text"
                            readOnly={channelType === 'BANK'}
                            placeholder={channelType === 'TILL' ? 'e.g. 123456' : channelType === 'BANK' ? 'Auto-filled upon bank selection' : 'e.g. 888880'}
                            className={`w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-emerald focus:ring-1 focus:ring-emerald outline-none transition-all font-mono ${channelType === 'BANK' ? 'bg-gray-100 text-gray-600 cursor-not-allowed font-bold' : ''}`}
                        />
                        {channelType === 'BANK' && <p className="text-[11px] text-gray-400">This paybill shortcode is automatically assigned for your selected bank.</p>}
                        {errors.channelNumber && <span className="text-[11px] text-red-500 font-medium">{errors.channelNumber.message as string}</span>}
                    </div>

                    {/* Account Number Reference (if PAYBILL or BANK) */}
                    {(channelType === 'PAYBILL' || channelType === 'BANK') && (
                        <div className="space-y-1 animate-in slide-in-from-top-2 duration-200">
                            <label className="text-xs font-bold text-gray-700 flex items-center gap-1">
                                <Hash className="w-3.5 h-3.5 text-gray-400" /> {channelType === 'BANK' ? 'Your Bank Account Number' : 'Account Number Reference'}
                            </label>
                            <input
                                {...register('accountNumber', { required: `${channelType === 'BANK' ? 'Bank Account Number' : 'Account Number Reference'} is required` })}
                                type="text"
                                placeholder={channelType === 'BANK' ? 'e.g. 01234567890123' : 'e.g. STORE-01 or Account Name'}
                                className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-emerald focus:ring-1 focus:ring-emerald outline-none transition-all font-mono"
                            />
                            <p className="text-[11px] text-gray-400">{channelType === 'BANK' ? 'Enter your exact bank account number where funds should be deposited.' : 'Optional reference tag for standard Paybill collections.'}</p>
                            {errors.accountNumber && <span className="text-[11px] text-red-500 font-medium">{errors.accountNumber.message as string}</span>}
                        </div>
                    )}

                    {/* Owner Phone */}
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-700 flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5 text-gray-400" /> Owner / Admin Phone Number
                        </label>
                        <input
                            {...register('ownerPhone', { required: 'Phone Number is required' })}
                            type="text"
                            placeholder="e.g. 0712345678 or 254712345678"
                            className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-emerald focus:ring-1 focus:ring-emerald outline-none transition-all font-mono"
                        />
                        <p className="text-[11px] text-gray-400">Used for verification and payment notifications.</p>
                        {errors.ownerPhone && <span className="text-[11px] text-red-500 font-medium">{errors.ownerPhone.message as string}</span>}
                    </div>

                    {registerMutation.isError && (
                        <div className="p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2 text-xs text-red-600">
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            <span>{(registerMutation.error as any)?.response?.data?.message || 'Failed to register payment channel. Please try again.'}</span>
                        </div>
                    )}

                    <div className="pt-3">
                        <button
                            type="submit"
                            disabled={registerMutation.isPending}
                            className="w-full h-11 flex items-center justify-center gap-2 rounded-xl text-white font-bold transition-all disabled:opacity-70 bg-emerald hover:bg-emerald/90 shadow-sm shadow-emerald/20"
                        >
                            {registerMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                            Connect Payment Channel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
